
import { useCallback, useEffect, useState } from 'react';
import { restoreSelection, saveSelection } from '@/utils/selectionManager';
import { applyFontSize, applyTextColor, applyBackgroundColor, applyFontFamily } from '@/utils/robustFormatting';
import { debugEditor } from '@/config/debug';
import { dumpSelection } from '@/utils/selectionDump';
import { snapshotHTML } from '@/utils/domSnapshot';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type FontFamily = 'Times New Roman' | 'Arial' | 'Georgia' | 'Verdana' | 'Courier New';
export type FontSize = '10pt' | '11pt' | '12pt' | '14pt' | '16pt' | '18pt' | '20pt' | '24pt';

interface UseDocumentFormatProps {
  contentEditableRef: React.RefObject<HTMLDivElement>;
  onFormatChange?: () => void;
  saveSnapshot?: () => void;
}

function dropAbntDefaultFlag(rootEl?: HTMLElement) {
  if (!rootEl) return;
  if (rootEl.getAttribute('data-abnt-default') === 'true') {
    rootEl.setAttribute('data-abnt-default', 'false');
  }
}

export const useDocumentFormat = ({ 
  contentEditableRef, 
  onFormatChange,
  saveSnapshot
}: UseDocumentFormatProps) => {
  const [mountLogged, setMountLogged] = useState(false);

  // Log de import para prova de uso do robustFormatting
  useEffect(() => {
    if (!mountLogged) {
      try {
        debugEditor.import({ 
          component: 'useDocumentFormat',
          robustFormattingImported: true,
          functions: ['applyFontSize', 'applyTextColor', 'applyBackgroundColor']
        });
      } catch (e) {
        debugEditor.import({ 
          component: 'useDocumentFormat',
          robustFormattingImported: false,
          error: e
        });
      }
      setMountLogged(true);
    }
  }, [mountLogged]);

  const executeCommand = useCallback((command: string, value?: string) => {
    if (!contentEditableRef.current) return false;

    try {
      // Garante que o elemento está focado
      contentEditableRef.current.focus();
      
      // Executa o comando
      const success = document.execCommand(command, false, value);
      
      if (success) {
        console.log(`Format: ${command} executed successfully`);
        onFormatChange?.();
        
        // Dispara evento para notificar mudanças
        const event = new Event('input', { bubbles: true });
        contentEditableRef.current.dispatchEvent(event);
      }
      
      return success;
    } catch (error) {
      console.error(`Format: Error executing ${command}:`, error);
      return false;
    }
  }, [contentEditableRef, onFormatChange]);

  const toggleBold = useCallback(() => {
    return executeCommand('bold');
  }, [executeCommand]);

  const toggleItalic = useCallback(() => {
    return executeCommand('italic');
  }, [executeCommand]);

  const toggleUnderline = useCallback(() => {
    return executeCommand('underline');
  }, [executeCommand]);

  const toggleStrikethrough = useCallback(() => {
    return executeCommand('strikeThrough');
  }, [executeCommand]);

  const setTextAlign = useCallback((alignment: TextAlign) => {
    const commands = {
      left: 'justifyLeft',
      center: 'justifyCenter',
      right: 'justifyRight',
      justify: 'justifyFull'
    };
    
    return executeCommand(commands[alignment]);
  }, [executeCommand]);

  const setFontFamily = useCallback((fontFamily: FontFamily) => {
    const root = contentEditableRef.current as HTMLElement | null;
    if (!root) return false;
    
    dropAbntDefaultFlag(root);
    
    const beforeDump = dumpSelection(root);
    const beforeSnapshot = snapshotHTML(root, 200);
    
    debugEditor.format({ 
      action: 'fontFamily', 
      value: fontFamily, 
      hasSelection: beforeDump.hasSelection,
      collapsed: beforeDump.collapsed,
      rangeTextLen: beforeDump.text.length
    });
    
    try {
      // Restaura seleção salva
      const restored = restoreSelection(root);
      
      if (!restored) {
        debugEditor.format({ action: 'setFontFamily', issue: 'no_selection_to_restore' });
        return false;
      }
      
      // Aplica fonte usando formatação robusta
      const success = applyFontFamily(fontFamily, root);
      
      if (success) {
        debugEditor.format({ action: 'setFontFamily', success: true });
        
        // Snapshot após aplicação
        if (saveSnapshot) {
          saveSnapshot();
        }
        
        return true;
      }
      
      // Fallback: usa execCommand tradicional
      debugEditor.format({ action: 'setFontFamily', fallback: 'execCommand' });
      const fallbackSuccess = executeCommand('fontName', fontFamily);
      
      if (fallbackSuccess && saveSnapshot) {
        saveSnapshot();
      }
      
      return fallbackSuccess;
      
    } catch (error) {
      debugEditor.format({ action: 'setFontFamily', error: String(error) });
      return false;
    }
  }, [executeCommand, saveSnapshot]);

  const setFontSize = useCallback((fontSize: FontSize) => {
    const root = contentEditableRef.current as HTMLElement | null;
    if (!root) return false;
    
    dropAbntDefaultFlag(root);
    
    const beforeDump = dumpSelection(root);
    const beforeSnapshot = snapshotHTML(root, 200);
    
    debugEditor.format({ 
      action: 'fontSize', 
      value: fontSize, 
      hasSelection: beforeDump.hasSelection,
      collapsed: beforeDump.collapsed,
      rangeTextLen: beforeDump.text.length,
      beforeContent: beforeSnapshot.innerHTML.substring(0, 100)
    });
    
    try {
      // Salva seleção e aplica formatação robusta
      saveSelection(root);
      
      // Se não há seleção ativa, foca e cria uma seleção no cursor
      const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.getRangeAt(0).collapsed) {
      debugEditor.format({ action: 'setFontSize', issue: 'no_selection_creating_artificial' });
      
      const range = document.createRange();
      range.selectNodeContents(root);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    
    const success = applyFontSize(fontSize, root);
    
    if (success) {
      const afterSnapshot = snapshotHTML(root, 200);
      debugEditor.format({ 
        action: 'fontSize', 
        result: 'success',
        afterContent: afterSnapshot.innerHTML.substring(0, 100),
        contentChanged: beforeSnapshot.innerHTML !== afterSnapshot.innerHTML
      });
      
      onFormatChange?.();
      saveSnapshot?.();
      debugEditor.snapshot({ action: 'fontSize', snapshotSaved: true });
      root.focus({ preventScroll: true });
      const event = new Event('input', { bubbles: true });
      root.dispatchEvent(event);
      return success;
    } else {
      debugEditor.format({ action: 'setFontSize', fallback: 'using_execCommand' });
      root.focus();
      const sizeMap = {
        '10pt': '1', '11pt': '2', '12pt': '3', '14pt': '4',
        '16pt': '5', '18pt': '6', '20pt': '7', '24pt': '7'
      };
      return executeCommand('fontSize', sizeMap[fontSize]);
    }
    } catch (error) {
      console.error('Format: Error setting font size:', error);
      debugEditor.format({ action: 'fontSize', result: 'error', error });
      // Fallback para execCommand
      const sizeMap = {
        '10pt': '1', '11pt': '2', '12pt': '3', '14pt': '4',
        '16pt': '5', '18pt': '6', '20pt': '7', '24pt': '7'
      };
      return executeCommand('fontSize', sizeMap[fontSize]);
    }
  }, [contentEditableRef, onFormatChange, executeCommand, saveSnapshot]);

  const setTextColor = useCallback((color: string) => {
    const root = contentEditableRef.current as HTMLElement | null;
    if (!root) return false;
    
    dropAbntDefaultFlag(root);
    
    const beforeDump = dumpSelection(root);
    const beforeSnapshot = snapshotHTML(root, 200);
    
    debugEditor.format({ 
      action: 'textColor', 
      value: color, 
      hasSelection: beforeDump.hasSelection,
      collapsed: beforeDump.collapsed,
      rangeTextLen: beforeDump.text.length
    });
    
    try {
      // Foca e garante seleção ANTES de aplicar formatação
      root.focus();
      
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.getRangeAt(0).collapsed) {
        debugEditor.format({ action: 'setTextColor', issue: 'no_selection_creating_artificial' });
        
        const range = document.createRange();
        range.selectNodeContents(root);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      
      const success = applyTextColor(color, root);
        
      if (success) {
        const afterSnapshot = snapshotHTML(root, 200);
        debugEditor.format({ 
          action: 'textColor', 
          result: 'success',
          contentChanged: beforeSnapshot.innerHTML !== afterSnapshot.innerHTML
        });
        
        console.log('DocumentFormat: Cor do texto aplicada com sucesso via robustFormatting');
        
        onFormatChange?.();
        saveSnapshot?.();
        debugEditor.snapshot({ action: 'textColor', snapshotSaved: true });
        root.focus({ preventScroll: true });
        const event = new Event('input', { bubbles: true });
        root.dispatchEvent(event);
        return success;
      } else {
        debugEditor.format({ action: 'setTextColor', fallback: 'using_execCommand' });
        root.focus();
        return executeCommand('foreColor', color);
      }
      
    } catch (error) {
      console.error('DocumentFormat: Erro ao aplicar cor do texto:', error);
      debugEditor.format({ action: 'textColor', result: 'error', error });
      root.focus();
      return executeCommand('foreColor', color);
    }
  }, [contentEditableRef, onFormatChange, executeCommand, saveSnapshot]);

  const setBackgroundColor = useCallback((color: string) => {
    const root = contentEditableRef.current as HTMLElement | null;
    if (!root) return false;
    
    dropAbntDefaultFlag(root);
    
    const beforeDump = dumpSelection(root);
    const beforeSnapshot = snapshotHTML(root, 200);
    
    debugEditor.format({ 
      action: 'bgColor', 
      value: color, 
      hasSelection: beforeDump.hasSelection,
      collapsed: beforeDump.collapsed,
      rangeTextLen: beforeDump.text.length
    });
    
    try {
      // Foca e garante seleção ANTES de aplicar formatação
      root.focus();
      
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.getRangeAt(0).collapsed) {
        debugEditor.format({ action: 'setBackgroundColor', issue: 'no_selection_creating_artificial' });
        
        const range = document.createRange();
        range.selectNodeContents(root);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      
      const success = applyBackgroundColor(color, root);
        
      if (success) {
        const afterSnapshot = snapshotHTML(root, 200);
        debugEditor.format({ 
          action: 'bgColor', 
          result: 'success',
          contentChanged: beforeSnapshot.innerHTML !== afterSnapshot.innerHTML
        });
        
        console.log('DocumentFormat: Cor de fundo aplicada com sucesso via robustFormatting');
        
        onFormatChange?.();
        saveSnapshot?.();
        debugEditor.snapshot({ action: 'bgColor', snapshotSaved: true });
        root.focus({ preventScroll: true });
        const event = new Event('input', { bubbles: true });
        root.dispatchEvent(event);
        return success;
      } else {
        debugEditor.format({ action: 'setBackgroundColor', fallback: 'using_execCommand' });
        root.focus();
        return executeCommand('backColor', color);
      }
      
    } catch (error) {
      console.error('DocumentFormat: Erro ao aplicar cor de fundo:', error);
      debugEditor.format({ action: 'bgColor', result: 'error', error });
      root.focus();
      return executeCommand('backColor', color);
    }
  }, [contentEditableRef, onFormatChange, executeCommand, saveSnapshot]);

  const insertUnorderedList = useCallback(() => {
    return executeCommand('insertUnorderedList');
  }, [executeCommand]);

  const insertOrderedList = useCallback(() => {
    return executeCommand('insertOrderedList');
  }, [executeCommand]);

  const indent = useCallback(() => {
    return executeCommand('indent');
  }, [executeCommand]);

  const outdent = useCallback(() => {
    return executeCommand('outdent');
  }, [executeCommand]);

  const removeFormat = useCallback(() => {
    return executeCommand('removeFormat');
  }, [executeCommand]);

  const getSelectionFormat = useCallback(() => {
    if (!contentEditableRef.current) return {};

    try {
      return {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikethrough: document.queryCommandState('strikeThrough'),
      };
    } catch (error) {
      console.error('Format: Error getting selection format:', error);
      return {};
    }
  }, [contentEditableRef]);

  return {
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrikethrough,
    setTextAlign,
    setFontFamily,
    setFontSize,
    setTextColor,
    setBackgroundColor,
    insertUnorderedList,
    insertOrderedList,
    indent,
    outdent,
    removeFormat,
    getSelectionFormat
  };
};
